import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {colors} from 'shells/colors/colors';
import {GlossaryBookmarkIcon} from 'shells/icon/svg/glossary-bookmark-icon';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {ProgressDonut} from 'shells/progress-bar/progress-donut';

import {useDismissibleUserSetting} from '../../../util/hooks/use-dismissible-user-setting';

import {useGetOnboardingChecklist} from '../graphql/hooks/use-get-onboarding-checklist';

import {toggleSidebarDismissed} from './redux/onboarding-actions';
import {getIsOnboardingSidebarDismissed} from './redux/onboarding-selectors';
import {UndoHideSidebarButton} from './undo-hide-sidebar-button';

const HAS_DISMISSED_ONBOARDING_SIDEBAR = 'has_dismissed_onboarding_sidebar';

type Props = {
    onToggleSidebar: () => void;
    isCollapsed?: boolean;
};

export function OnboardingChecklistButton(props: Props) {
    const {
        onboardingChecklist,
        completedCount,
        totalCount,
        checklistIsComplete,
        isLoading: isChecklistLoading,
    } = useGetOnboardingChecklist();

    const {shouldShow, isLoading} = useDismissibleUserSetting(HAS_DISMISSED_ONBOARDING_SIDEBAR);
    const dispatch = useDispatch();
    const sidebarWasJustDismissed = useSelector((state) => getIsOnboardingSidebarDismissed(state));

    // Only show the undo dismiss button for 5 seconds after the sidebar is dismissed
    React.useEffect(() => {
        setTimeout(() => {
            dispatch(toggleSidebarDismissed(false));
        }, 5000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sidebarWasJustDismissed]);

    // Open the sidebar by query param
    React.useEffect(() => {
        if (
            shouldShow &&
            window.location.pathname === '/dashboard' &&
            window.location.search.includes('onboarding=true')
        ) {
            props.onToggleSidebar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    if (sidebarWasJustDismissed) {
        return (
            <UndoHideSidebarButton
                isSidebarCollapsed={props.isCollapsed}
                onUndo={() => {
                    props.onToggleSidebar();
                    dispatch(toggleSidebarDismissed(false));
                }}
            />
        );
    }

    if (!shouldShow && !isLoading) {
        return null;
    }
    let buttonContent = null;
    if (props.isCollapsed) {
        buttonContent = (
            <div className='flex align-center justify-center full-height'>
                {checklistIsComplete ? (
                    // @ts-expect-error
                    <GlossaryBookmarkIcon size={15} color={colors.white} alignCenter={true} />
                ) : (
                    <ProgressDonut
                        percentComplete={
                            onboardingChecklist ? onboardingChecklist.percentComplete : 0
                        }
                        fontColor={colors.white}
                        height={32}
                        noPadding={true}
                    />
                )}
            </div>
        );
    } else {
        buttonContent = (
            <div className='flex align-center justify-sb full-height'>
                {checklistIsComplete ? (
                    <div className='flex align-center'>
                        <div className='mr-8'>
                            <GlossaryBookmarkIcon
                                size={15}
                                // @ts-expect-error
                                color={colors.white}
                                alignCenter={true}
                            />
                        </div>
                        <div className='nut-sidebar--onboarding-checklist-button--title'>
                            Explore more
                        </div>
                    </div>
                ) : (
                    <>
                        <div className='nut-sidebar--onboarding-checklist-button--content'>
                            <div className='nut-sidebar--onboarding-checklist-button--title'>
                                Getting started
                            </div>
                            <div className='nut-sidebar--onboarding-checklist-button--done'>
                                {completedCount || 0} of {totalCount || 0} done
                            </div>
                        </div>
                        <ProgressDonut
                            percentComplete={
                                onboardingChecklist ? onboardingChecklist.percentComplete : 0
                            }
                            fontColor={colors.white}
                            height={40}
                            noPadding={true}
                        />
                    </>
                )}
            </div>
        );
    }

    return (
        <div
            className={
                checklistIsComplete
                    ? 'nut-sidebar--onboarding-checklist-button--explore-more'
                    : 'nut-sidebar--onboarding-checklist-button'
            }
            onClick={props.onToggleSidebar}
        >
            {isLoading || isChecklistLoading ? (
                <div className='flex align-center justify-center full-height'>
                    <LoadingIcon fill={colors.onboardingHelpLt} size={24} />
                </div>
            ) : (
                buttonContent
            )}
        </div>
    );
}
